// 표준화된 LINE 차트 옵션 생성
import { formatValue } from '../../../utils/numberFormatter';
import { applyCommonStyles } from './applyCommonStyles';

export const generateLineChartOption = (data, baseOption, options = {}, isEditMode) => {
  // 표준 구조: { categories: [...], series: [{ name: "...", values: [...] }] }
  const categories = data.categories || [];
  const series = data.series || [];
  
  // 차트 크기에 따른 동적 nameGap 계산 (여백 최소화)
  const containerWidth = options.__containerWidth || 500;
  const containerHeight = options.__containerHeight || 400;
  const dynamicYNameGap = containerWidth < 400 ? 35 : (containerWidth < 600 ? 40 : 45);
  const dynamicXNameGap = containerHeight < 300 ? 20 : (containerHeight < 400 ? 25 : 30);
  
  // X축 설정
  baseOption.xAxis = {
    ...baseOption.xAxis,
    type: 'category',
    data: categories,
    name: options.axes?.x || '',
    nameLocation: 'middle',
    nameGap: dynamicXNameGap,
    nameTextStyle: {
      fontSize: 18,
      fontWeight: '600',
      color: '#2c3e50',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }
  };

  // 이중 Y축 지원
  const hasDualYAxis = options.advanced?.dualYAxis && typeof options.advanced.dualYAxis === 'object';
  
  if (hasDualYAxis) {
    
    // secondaryAxisSeries가 없고 시리즈가 2개인 경우 자동으로 두 번째 시리즈를 보조축에 할당
    if (!options.advanced?.secondaryAxisSeries && series.length === 2) {
      if (!options.advanced) options.advanced = {};
      options.advanced.secondaryAxisSeries = [series[1].name];
    }
    // 이중 Y축 설정
    baseOption.yAxis = [
      {
        type: 'value',
        name: options.advanced.dualYAxis.primary?.name || options.axes?.y || '',
        position: 'left',
        nameLocation: 'middle',
        nameGap: dynamicYNameGap,
        nameTextStyle: {
          fontSize: 18,
          fontWeight: '600',
          color: options.advanced.dualYAxis.primary?.color || '#5470c6',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: options.advanced.dualYAxis.primary?.color || '#5470c6',
            width: 2
          }
        },
        axisLabel: {
          show: true,
          color: options.advanced.dualYAxis.primary?.color || '#5470c6',
          fontSize: 14,
          formatter: function(value) {
            return formatValue(value);
          }
        },
        axisTick: {
          show: true,
          lineStyle: {
            color: options.advanced.dualYAxis.primary?.color || '#5470c6'
          }
        },
        min: options.advanced.dualYAxis.primary?.min,
        max: options.advanced.dualYAxis.primary?.max,
        interval: options.advanced.dualYAxis.primary?.interval,
        scale: options.advanced.dualYAxis.primary?.scale !== undefined ? options.advanced.dualYAxis.primary.scale : false
      },
      {
        type: 'value',
        show: true,
        name: options.advanced.dualYAxis.secondary?.name || '',
        position: 'right',
        offset: 0,
        nameLocation: 'middle',
        nameGap: dynamicYNameGap,
        nameTextStyle: {
          fontSize: 18,
          fontWeight: '600',
          color: options.advanced.dualYAxis.secondary?.color || '#91cc75',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: options.advanced.dualYAxis.secondary?.color || '#91cc75',
            width: 2
          }
        },
        axisLabel: {
          show: true,
          color: options.advanced.dualYAxis.secondary?.color || '#91cc75',
          fontSize: 14,
          formatter: function(value) {
            return formatValue(value);
          }
        },
        axisTick: {
          show: true,
          lineStyle: {
            color: options.advanced.dualYAxis.secondary?.color || '#91cc75'
          }
        },
        splitLine: {
          show: false
        },
        min: options.advanced.dualYAxis.secondary?.min,
        max: options.advanced.dualYAxis.secondary?.max,
        interval: options.advanced.dualYAxis.secondary?.interval,
        scale: options.advanced.dualYAxis.secondary?.scale !== undefined ? options.advanced.dualYAxis.secondary.scale : true
      }
    ];
    
    // containLabel: true가 양쪽 Y축 제목 길이를 자동으로 측정하여 여백을 계산하므로
    // 하드코딩된 grid.left/right 설정은 제거 (근본적 해결)
  } else {
    // 단일 Y축 설정
    baseOption.yAxis = {
      ...baseOption.yAxis,
      type: 'value',
      name: options.axes?.y || '',
      nameLocation: 'middle',
      nameGap: dynamicYNameGap,
      nameTextStyle: {
        fontSize: 18,
        fontWeight: '600',
        color: '#2c3e50',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      },
      axisLabel: {
        formatter: function(value) {
          return formatValue(value);
        }
      },
      // 고급 옵션 지원
      min: options.advanced?.yMin,
      max: options.advanced?.yMax,
      interval: options.advanced?.yInterval,
      scale: options.advanced?.yScale !== undefined ? options.advanced.yScale : false
    };
  }

  // containLabel: true가 Y축 제목 길이를 자동으로 측정하여 여백을 계산하므로
  // 하드코딩된 grid.left 설정은 제거 (근본적 해결)

  // 시리즈 설정
  // 데이터 라벨 표시 설정 및 포맷터
  const showDataLabels = options.advanced?.showDataLabels === true;
  const labelFormat = options.advanced?.labelFormat || '{value}';
  const unitText = options.unit || '';

  const makeLineLabelFormatter = () => (params) => {
    const formatted = formatValue(params.value);
    return labelFormat
      .replace('{name}', params.name ?? '')
      .replace('{seriesName}', params.seriesName ?? '')
      .replace('{value}', formatted)
      .replace('{unit}', unitText);
  };

  baseOption.series = series.map((seriesItem, index) => {
    // secondaryAxisSeries 배열 체크
    const secondaryAxisSeries = options.advanced?.secondaryAxisSeries || [];
    const useSecondaryAxis = seriesItem.useSecondaryAxis || secondaryAxisSeries.includes(seriesItem.name);
    
    // 라인 스무딩 옵션 처리 (기본값: true)
    const isSmooth = options.advanced?.lineSmooth !== false;
    
    if (hasDualYAxis && useSecondaryAxis) {
    }
    
    if (isSmooth) {
    }
    
    const colors = options.style?.colors || ['#4CAF50', '#2196F3', '#FFC107', '#E91E63', '#9C27B0'];
    return {
      name: seriesItem.name || '데이터',
      type: 'line',
      data: (seriesItem.values || []).map((v) => (typeof v === 'object' && v !== null && v.value !== undefined) ? { value: v.value, __row: v.__row, __cols: v.__cols, __extras: v.__extras } : v),
      smooth: isSmooth,
      symbolSize: 6,
      lineStyle: {
        color: seriesItem.lineColor || colors[index % colors.length],
        width: seriesItem.lineWidth || 2
      },
      itemStyle: {
        color: seriesItem.color || colors[index % colors.length]
      },
      label: {
        show: showDataLabels,
        position: options.advanced?.labelPosition || 'top',
        formatter: makeLineLabelFormatter()
      },
      // 이중 Y축 지원: useSecondaryAxis 속성에 따라 Y축 인덱스 결정
      yAxisIndex: hasDualYAxis && useSecondaryAxis ? 1 : 0,
      emphasis: { 
        focus: 'series',
        itemStyle: { borderWidth: 2 }
      }
    };
  });

  // 범례 설정 - showLegend 옵션이 true이거나 다중 시리즈인 경우 표시
  const showLegend = options.advanced?.showLegend === true || series.length > 1;
  
  if (showLegend || options.advanced?.legendPosition) {
    // 범례 위치 옵션 처리
    const legendPosition = options.advanced?.legendPosition || 'bottom';
    
    // 범례 순서 설정 (legendOrder가 있으면 해당 순서로, 없으면 series 순서대로)
    let legendData;
    if (options.advanced?.legendOrder && Array.isArray(options.advanced.legendOrder)) {
      // 사용자 지정 순서가 있는 경우
      const seriesMap = new Map(series.map(s => [s.name || '데이터', s]));
      legendData = options.advanced.legendOrder.filter(name => seriesMap.has(name));
      // legendOrder에 없는 시리즈들은 뒤에 추가
      const remainingSeries = series.filter(s => !options.advanced.legendOrder.includes(s.name || '데이터'));
      legendData = [...legendData, ...remainingSeries.map(s => s.name || '데이터')];
    } else {
      // 기본: series 순서대로
      legendData = series.map(s => s.name || '데이터');
    }
    
    const legendConfig = {
      data: legendData,
      type: 'scroll'
    };

    // 위치별 설정 - 각 케이스에서 모든 방향 속성을 명시적으로 설정
    switch(legendPosition) {
      case 'top':
        legendConfig.top = 10;
        legendConfig.bottom = undefined;
        legendConfig.left = 'center';
        legendConfig.right = undefined;
        legendConfig.orient = 'horizontal';
        break;
      case 'bottom':
        legendConfig.top = undefined;
        legendConfig.bottom = 10;
        legendConfig.left = 'center';
        legendConfig.right = undefined;
        legendConfig.orient = 'horizontal';
        break;
      case 'left':
        legendConfig.top = 'middle';
        legendConfig.bottom = undefined;
        legendConfig.left = 10;
        legendConfig.right = undefined;
        legendConfig.orient = 'vertical';
        break;
      case 'right':
        legendConfig.top = 'middle';
        legendConfig.bottom = undefined;
        legendConfig.left = undefined;
        legendConfig.right = 10;
        legendConfig.orient = 'vertical';
        break;
      case 'top-right':
        legendConfig.top = '5%';
        legendConfig.right = '5%';
        legendConfig.orient = 'vertical';
        legendConfig.backgroundColor = 'rgba(255, 255, 255, 0.9)';
        legendConfig.borderColor = '#ddd';
        legendConfig.borderWidth = 1;
        legendConfig.borderRadius = 4;
        legendConfig.padding = [5, 10];
        legendConfig.itemGap = 5;
        break;
      case 'top-left':
        legendConfig.top = '5%';
        legendConfig.left = '5%';
        legendConfig.orient = 'vertical';
        legendConfig.backgroundColor = 'rgba(255, 255, 255, 0.9)';
        legendConfig.borderColor = '#ddd';
        legendConfig.borderWidth = 1;
        legendConfig.borderRadius = 4;
        legendConfig.padding = [5, 10];
        legendConfig.itemGap = 5;
        break;
    }

    baseOption.legend = legendConfig;

    // 범례 위치에 따른 grid 여백 조정 (범례와 차트 영역 겹침 방지)
    if (legendPosition === 'right') {
      baseOption.grid = { ...baseOption.grid, right: (baseOption.grid?.right || 30) + 80 };
    } else if (legendPosition === 'left') {
      baseOption.grid = { ...baseOption.grid, left: (baseOption.grid?.left || 50) + 80 };
    } else if (legendPosition === 'top' || legendPosition === 'top-left' || legendPosition === 'top-right') {
      baseOption.grid = { ...baseOption.grid, top: (baseOption.grid?.top || 30) + 30 };
    }
  } else {
    delete baseOption.legend;
  }

  // 디버깅을 위한 최종 옵션 출력
  if (hasDualYAxis) {
  }

  // transparent 처리를 먼저 수행 (applyCommonStyles 전에)
  // showFrame은 DashboardComponentRenderer에서 처리하므로 여기서는 transparent만 처리
  // 툴팁 순서 설정 (legendOrder와 동일하게)
  if (options.advanced?.legendOrder && Array.isArray(options.advanced.legendOrder) && series.length > 1) {
    baseOption.tooltip = {
      ...baseOption.tooltip,
      formatter: function(params) {
        if (!Array.isArray(params)) params = [params];
        
        let tooltipHtml = '';
        if (params[0] && params[0].name) {
          tooltipHtml += `${params[0].name}<br/>`;
        }
        
        // legendOrder 순서대로 정렬
        const seriesMap = new Map(series.map(s => [s.name || '데이터', s]));
        const orderedSeriesNames = options.advanced.legendOrder.filter(name => seriesMap.has(name));
        const remainingSeriesNames = series.filter(s => !options.advanced.legendOrder.includes(s.name || '데이터')).map(s => s.name || '데이터');
        const allOrderedNames = [...orderedSeriesNames, ...remainingSeriesNames];
        
        // 순서대로 툴팁 항목 생성
        allOrderedNames.forEach(seriesName => {
          const param = params.find(p => p.seriesName === seriesName);
          if (param) {
            const value = typeof param.value === 'object' && param.value !== null && param.value.value !== undefined
              ? param.value.value
              : param.value;
            const formattedValue = formatValue(value);
            tooltipHtml += `${param.marker}${param.seriesName}: <b>${formattedValue}</b><br/>`;
          }
        });
        
        return tooltipHtml;
      }
    };
  }

  // transparent 옵션 체크 (boolean true 또는 string "true" 모두 처리)
  const isTransparent = options.transparent === true || options.transparent === 'true';
  
  if (isTransparent) {
    // ECharts 전체 배경과 그리드 배경을 모두 투명하게 설정
    baseOption.backgroundColor = 'transparent';
    if (baseOption.grid) {
      baseOption.grid.backgroundColor = 'transparent';
    }
  }

  // 공통 스타일 적용 (transparent 설정 후에 적용)
  if (options.style) {
    baseOption = applyCommonStyles(baseOption, options.style);
  }
  
  // 사용자가 advanced.grid로 여백을 직접 지정하면 최종 오버라이드
  if (options.advanced?.grid && typeof options.advanced.grid === 'object') {
    baseOption.grid = { ...baseOption.grid, ...options.advanced.grid };
  }
  return baseOption;
}; 